import React from 'react'
import { Home, Tag, ShoppingCart, ShoppingBag, User } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@/store/store'
import { useNavigate } from 'react-router-dom'
import { handleBottomSheetSelect } from '@/store/features/state'

interface MenuItem {
	id: string
	icon: LucideIcon
	label: string
	href: string
	isCenter?: boolean
	badge?: number
}

const MenuBottom: React.FC = () => {
	const navigate = useNavigate()
	const { totalItems } = useSelector((state: RootState) => state.cart)
	const dispatch = useDispatch<AppDispatch>()
	const bottomSheetSelect = useSelector(
		(state: RootState) => state.state.bottomSheetSelect
	)

	const menuItems: MenuItem[] = [
		{
			id: 'home',
			icon: Home,
			label: 'Inicio',
			href: '/',
		},
		{
			id: 'promotions',
			icon: Tag,
			label: 'Ofertas',
			href: '/Promociones',
			badge: 1,
		},
		{
			id: 'cart',
			icon: ShoppingCart,
			label: 'Carrito',
			href: '/Carrito',
			isCenter: true,
			badge: totalItems,
		},
		{
			id: 'orders',
			icon: ShoppingBag,
			label: 'Pedidos',
			href: '/Perfil/compras',
		},
		{
			id: 'profile',
			icon: User,
			label: 'Perfil',
			href: '/Perfil/informacion',
		},
	]

	const handleItemClick = (itemId: string, href: string) => {
		dispatch(handleBottomSheetSelect(itemId))
		navigate(href!)
	}

	const renderMenuItem = (item: MenuItem) => {
		const IconComponent = item.icon
		const isActive = bottomSheetSelect === item.id
		const isCenter = item.isCenter

		if (isCenter) {
			return (
				<div key={item.id} className='relative flex flex-col items-center'>
					<button
						onClick={() => handleItemClick(item.id, item.href)}
						className='relative w-16 h-16 -mt-8 bg-gradient-to-br from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 rounded-full shadow-2xl hover:shadow-red-500/40 transform hover:-translate-y-1 transition-all duration-300 ease-out flex items-center justify-center group active:scale-95'
						aria-label={item.label}
					>
						<div className='absolute inset-0 rounded-full bg-red-500 animate-pulse opacity-75 group-hover:opacity-100'></div>

						<IconComponent
							size={28}
							className='relative z-[9992] text-white drop-shadow-sm'
						/>

						{item.badge && item.badge > 0 && (
							<div className='absolute -top-2 -right-2 w-6 h-6 bg-yellow-400 text-black text-xs font-bold rounded-full flex items-center justify-center shadow-lg border-2 border-white animate-bounce'>
								{item.badge > 9 ? '9+' : item.badge}
							</div>
						)}
					</button>

					<span
						className={`mt-2 text-xs font-medium transition-colors duration-200 ${
							isActive ? 'text-red-600' : 'text-gray-600'
						}`}
					>
						{item.label}
					</span>
				</div>
			)
		}

		return (
			<button
				key={item.id}
				onClick={() => handleItemClick(item.id, item.href)}
				className='relative flex flex-col items-center justify-center p-2 min-w-0 flex-1 group active:scale-95 transition-transform duration-150'
				aria-label={item.label}
			>
				<div
					className={`absolute top-0 left-1/2 transform -translate-x-1/2 w-8 h-1 rounded-full transition-all duration-300 ${
						isActive
							? 'bg-red-500 opacity-100 scale-100'
							: 'bg-transparent opacity-0 scale-50'
					}`}
				/>

				<div className='relative'>
					<div
						className={`p-2.5 rounded-xl transition-all duration-300 ${
							isActive
								? 'bg-red-50 text-red-600 scale-110 shadow-lg shadow-red-100'
								: 'text-gray-600 hover:bg-gray-100 hover:text-gray-800 hover:scale-105'
						}`}
					>
						<IconComponent
							size={22}
							className='transition-colors duration-200'
						/>
					</div>

					{item.badge && item.badge > 0 && !isCenter && (
						<div className='absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center shadow-lg border border-white'>
							{item.badge > 9 ? '9+' : item.badge}
						</div>
					)}
				</div>

				<span
					className={`mt-1 text-xs font-medium transition-colors duration-200 text-center leading-tight truncate max-w-full ${
						isActive
							? 'text-red-600'
							: 'text-gray-600 group-hover:text-gray-800'
					}`}
				>
					{item.label}
				</span>
			</button>
		)
	}

	return (
		<>
			<div className='h-20 lg:hidden' />

			<div className='fixed bottom-0 left-0 right-0 z-[9992] lg:hidden border-t border-gray-200 bg-white'>
				<div className='absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent opacity-50' />

				<div className='relative bg-white/95 backdrop-blur-md'>
					<div className='relative flex items-end justify-around'>
						{menuItems.map(renderMenuItem)}
					</div>
				</div>

				<div className='h-2 bg-white sm:h-4' />
			</div>
		</>
	)
}

export default MenuBottom
